import { createSelector } from "reselect";
import { IRootReducer } from "../../reducers";
import { Agency, AgencyAbbr, AgencyId, getAgencyId } from "../../reducers/states/agency";
import { useSelector } from "react-redux";

// SELECTORS

/**
 * Base selectors
 */
const selectAgencyState = (state: IRootReducer) =>
  state.agencyReducer;

const selectAgenciesById = createSelector(
  [selectAgencyState],
  agencyState => agencyState.agenciesById
);

const selectAllAgencyIds = createSelector(
  [selectAgencyState],
  agencyState => agencyState.agencyIds
);

/**
 * All agencies (denormalized)
 * Recomputes only when agenciesById or allAgencyIds change.
 */
const selectAgencies = createSelector(
  [selectAgenciesById, selectAllAgencyIds],
  (agenciesById, ids): Agency[] => ids.map(id => agenciesById[id])
);

/**
 * All user agencies (denormalized)
 * Recomputes only when agenciesById or allAgencyIds change.
 */
const selectUserAgencies = createSelector(
  [selectAgenciesById, selectAllAgencyIds],
  (agenciesById, ids): Agency[] => { 
    return ids.map(id => agenciesById[id])
      .filter(a => a.isUserAgency)
      .sort((a1, a2) => a1!.name > a2!.name ? 1 : -1) 
  }
);

/**
 * Single agency by ID (selector factory)
 * Perfect for child components or isolated lookups.
 */
const makeSelectAgencyById = (id: string) => {
  return createSelector([selectAgenciesById], agenciesById => agenciesById[id]);
};

/**
 * Bulk lookup by array of IDs
 * Ideal for components that need many agencies at once.
 */
const makeSelectAgenciesByIds = (ids: string[]) => {
  return createSelector([selectAgenciesById], agenciesById =>
    ids.map(id => agenciesById[id])
  );
};

// HOOKS

/**
 * Get ALL agencies (denormalized array)
 * Best for bulk rendering.
 */
export const useAgencies = (): Agency[] => {
  const selector = useSelector(selectAgencies);
  return selector;
};

/**
 * Get all agencies where isUserAgency is true (denormalized array)
 * Best for bulk rendering.
 */
export const useUserAgencies = (): Agency[] => {
  const selector = useSelector(selectUserAgencies);
  return selector;
};

/**
 * Get ONE agency by ID
 * Best used inside child components.
 * @param id An actual ID or "legacy ID" (certain AgencyAbbr values).
 * @returns The agency matching the value of the provided "id" param, or undefined.
 */
export const useAgency = (id: AgencyId | AgencyAbbr | undefined): Agency | undefined => {
  // Backwards compatible conversion in case value of id is a "legacy ID"
  id = getAgencyId(id || "");
  
  const selector = useSelector(makeSelectAgencyById(id));
  return selector;
};

/**
 * Get MANY agencies by an array of IDs
 * Best for bulk lookups when you have a list of IDs.
 */
export const useAgenciesByIds = (ids: string[]): Agency[] => {
  // Backwards compatible conversion in case any value is an "Old ID"
  const compatibleIds = ids.map(id => getAgencyId(id));

  return useSelector(makeSelectAgenciesByIds(compatibleIds));
};
